import React, { useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import API from "../../Common/API"
import {
    HiOutlineSparkles, HiOutlineMagnifyingGlass, HiOutlineUserCircle,
    HiOutlineCheckCircle, HiOutlineArrowRight, HiOutlineXMark,
    HiOutlineArrowPath,
} from "react-icons/hi2"
import { RiWhatsappLine } from "react-icons/ri"

// ── helpers ──────────────────────────────────────────────────────────────────
const normalizePhone = (v) => (v || "").toString().replace(/\D/g, "")

const ageFrom = (dob) => {
    if (!dob) return null
    const d = new Date(dob)
    if (isNaN(d)) return null
    return Math.floor((Date.now() - d) / (365.25 * 24 * 3600 * 1000))
}

const VERDICT_STYLE = {
    excellent:    { pill: "bg-green-50 border-green-200 text-green-700",   bar: "bg-green-500" },
    good:         { pill: "bg-blue-50 border-blue-200 text-blue-700",      bar: "bg-blue-500" },
    average:      { pill: "bg-amber-50 border-amber-200 text-amber-700",   bar: "bg-amber-400" },
    incompatible: { pill: "bg-red-50 border-red-200 text-red-600",         bar: "bg-red-400" },
}
const vs = (v) => VERDICT_STYLE[v] || VERDICT_STYLE.average

const PoruthamBar = ({ porutham }) => {
    if (!porutham) return (
        <span className="text-xs text-tpColor italic">Star not set — porutham unavailable</span>
    )
    const pct = Math.round((porutham.total / porutham.max) * 100)
    const s = vs(porutham.verdict)
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${s.pill}`}>
                    <HiOutlineSparkles className="h-3 w-3" />
                    {porutham.total}/{porutham.max} · {porutham.verdict}
                </span>
                <span className="text-xs font-bold text-tpColor">{pct}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full transition-all ${s.bar}`} style={{ width: `${pct}%` }} />
            </div>
            {porutham.breakdown && (
                <div className="flex flex-wrap gap-1 mt-0.5">
                    {porutham.breakdown.map((p) => (
                        <span key={p.name}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${p.score ? "bg-green-50 text-green-700" : "bg-red-50 text-red-500 line-through opacity-60"}`}>
                            {p.name}
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}

// ── UserSearchPanel ───────────────────────────────────────────────────────────
const UserSearchPanel = ({ onSelect }) => {
    const [query, setQuery] = useState("")
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const timerRef = useRef(null)

    useEffect(() => {
        clearTimeout(timerRef.current)
        if (!query.trim()) { setResults([]); return }
        timerRef.current = setTimeout(async () => {
            setLoading(true)
            try {
                const res = await API.get("/user/getUserList", { params: { page: 1, limit: 20, search: query.trim() } })
                const docs = res.data?.data?.docs || res.data?.data || []
                // Exclude married (Happy Customer, status 4) from search
                const active = Array.isArray(docs) ? docs.filter((u) => u?.status !== 4) : []
                setResults(active)
            } catch { setResults([]) }
            finally { setLoading(false) }
        }, 350)
        return () => clearTimeout(timerRef.current)
    }, [query])

    return (
        <div className="flex flex-col gap-4">
            <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor mb-1">Step 1</p>
                <h3 className="text-lg font-semibold text-tColor">Select a member to find matches for</h3>
                <p className="text-sm text-tpColor mt-0.5">Search by name, mobile number, or Ref ID</p>
            </div>

            <div className="relative">
                <HiOutlineMagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tpColor" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search member…"
                    className="w-full rounded-xl border border-borderColor bg-white py-2.5 pl-9 pr-4 text-sm text-tColor outline-none placeholder:text-tpColor/60 focus:border-primeColor"
                />
                {loading && (
                    <HiOutlineArrowPath className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-primeColor" />
                )}
            </div>

            <div className="flex flex-col gap-2 max-h-[460px] overflow-y-auto pr-0.5">
                {results.length === 0 && query.trim() && !loading && (
                    <p className="rounded-xl border border-dashed border-borderColor px-4 py-5 text-sm text-center text-tpColor">
                        No members found for "{query}"
                    </p>
                )}
                {results.map((user) => {
                    const mobile = user.mobileNumber || user.contactInformation?.mobileNumber || ""
                    const age = ageFrom(user.dateOfBirth)
                    return (
                        <button
                            key={user._id}
                            type="button"
                            onClick={() => onSelect(user)}
                            className="flex items-center gap-3 rounded-xl border border-borderColor bg-white px-4 py-3 text-left transition hover:border-primeColor hover:bg-primeSoft/30"
                        >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-tpColor">
                                <HiOutlineUserCircle className="h-6 w-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate font-semibold text-tColor text-sm">
                                    {user.firstName} {user.surname || user.lastName || ""}
                                    <span className="ml-1.5 text-xs font-normal text-tpColor">· {user.refId}</span>
                                </p>
                                <p className="text-xs text-tpColor mt-0.5">
                                    {[mobile, age && `${age} yrs`, user.profileType].filter(Boolean).join(" · ")}
                                </p>
                            </div>
                            <HiOutlineArrowRight className="h-4 w-4 shrink-0 text-tpColor" />
                        </button>
                    )
                })}
                {!query.trim() && (
                    <div className="rounded-xl border border-dashed border-borderColor bg-slate-50/60 px-4 py-6 text-sm text-center text-tpColor">
                        Start typing to search members
                    </div>
                )}
            </div>
        </div>
    )
}

// ── MatchResultsPanel ─────────────────────────────────────────────────────────
const MatchResultsPanel = ({ targetUser, onBack }) => {
    const [matches, setMatches] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState("")
    const [selectedIds, setSelectedIds] = useState([])
    const [sending, setSending] = useState(false)

    const targetMobile = normalizePhone(
        targetUser.mobileNumber || targetUser.contactInformation?.whatsappNumber || targetUser.contactInformation?.mobileNumber || ""
    )
    const whatsappNumber = targetMobile.length === 10 ? `91${targetMobile}` : targetMobile

    const fetchMatches = async () => {
        setLoading(true)
        setSelectedIds([])
        try {
            const res = await API.get("/matches/getSuggestions", {
                params: { limit: 50, userId: targetUser._id }
            })
            setMatches(res.data?.data || [])
        } catch { setMatches([]) }
        finally { setLoading(false) }
    }

    useEffect(() => { fetchMatches() }, [targetUser._id]) // eslint-disable-line

    const filtered = useMemo(() => {
        // Only show profiles where porutham is 7 or above
        const withPorutham = matches.filter((m) => m.porutham && m.porutham.total >= 7)
        if (!search.trim()) return withPorutham
        const q = search.toLowerCase()
        return withPorutham.filter((m) =>
            [m.firstName, m.lastName, m.surname, m.refId, m.starName, m.star, m.mobileNumber, m.contactInformation?.mobileNumber]
                .filter(Boolean).join(" ").toLowerCase().includes(q)
        )
    }, [matches, search])

    const allFilteredSelected = filtered.length > 0 && filtered.every((m) => selectedIds.includes(m._id))

    const toggleAll = () => {
        if (allFilteredSelected) {
            setSelectedIds((ids) => ids.filter((id) => !filtered.some((m) => m._id === id)))
        } else {
            const toAdd = filtered.map((m) => m._id).filter((id) => !selectedIds.includes(id))
            setSelectedIds((ids) => [...ids, ...toAdd])
        }
    }

    const toggleOne = (id) =>
        setSelectedIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id])

    const selectedMatches = useMemo(
        () => matches.filter((m) => selectedIds.includes(m._id)),
        [matches, selectedIds]
    )

    const buildWhatsAppMessage = () => {
        const name = [targetUser.firstName, targetUser.surname || targetUser.lastName].filter(Boolean).join(" ").trim() || "Member"
        const lines = [
            `Hello ${name},`,
            "",
            `Here are ${selectedMatches.length} matching profile${selectedMatches.length > 1 ? "s" : ""} with Jathaga Porutham from Royal Kavery Matrimony:`,
            "",
            ...selectedMatches.map((m, i) => {
                const mName = [m.firstName, m.surname || m.lastName].filter(Boolean).join(" ").trim() || "Profile"
                const age = ageFrom(m.dateOfBirth)
                const porutham = m.porutham ? `Porutham: ${m.porutham.total}/${m.porutham.max} (${m.porutham.verdict})` : "Porutham: Star not set"
                const star = m.starName || m.star || m.additionalInformation?.starName || ""
                const occ = m.occupationDetail || m.occupation || ""
                const parts = [age && `Age ${age}`, star && `Star: ${star}`, occ, porutham].filter(Boolean)
                return `${i + 1}. ${mName} | Ref: ${m.refId || "-"}\n   ${parts.join(" | ")}`
            }),
            "",
            "Please let us know if you are interested in any of these profiles.",
            "",
            "Regards,\nRoyal Kavery Matrimony",
        ]
        return lines.join("\n")
    }

    const sendOnWhatsApp = () => {
        if (!whatsappNumber || selectedMatches.length === 0) return
        setSending(true)
        const msg = buildWhatsAppMessage()
        window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer")
        setTimeout(() => setSending(false), 1500)
    }

    return (
        <div className="flex flex-col gap-4 h-full">
            {/* Header */}
            <div className="flex items-start gap-3">
                <button type="button" onClick={onBack}
                    className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-borderColor bg-white text-tpColor transition hover:border-primeColor hover:text-primeColor">
                    <HiOutlineXMark className="h-4 w-4" />
                </button>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor">Step 2 — Matches</p>
                    <h3 className="text-lg font-semibold text-tColor truncate">
                        {targetUser.firstName} {targetUser.surname || targetUser.lastName || ""}
                        <span className="ml-1.5 text-sm font-normal text-tpColor">· {targetUser.refId}</span>
                    </h3>
                    <p className="text-xs text-tpColor mt-0.5">
                        WhatsApp: {targetUser.mobileNumber || targetUser.contactInformation?.mobileNumber || "—"}
                        {" · "}{matches.filter(m => m.porutham && m.porutham.total >= 7).length} matches found
                    </p>
                </div>
                <button type="button" onClick={fetchMatches} title="Refresh matches"
                    className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-borderColor bg-white text-tpColor transition hover:border-primeColor hover:text-primeColor">
                    <HiOutlineArrowPath className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                    <HiOutlineMagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tpColor" />
                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter matches…"
                        className="w-full rounded-xl border border-borderColor bg-white py-2 pl-9 pr-3 text-sm text-tColor outline-none placeholder:text-tpColor/60 focus:border-primeColor" />
                </div>
                <button type="button" onClick={toggleAll}
                    className={`flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-bold transition ${allFilteredSelected ? "border-primeColor bg-primeSoft text-primeColor" : "border-borderColor bg-white text-tpColor hover:border-primeColor hover:text-primeColor"}`}>
                    <HiOutlineCheckCircle className="h-4 w-4" />
                    {allFilteredSelected ? "Deselect All" : `Select All (${filtered.length})`}
                </button>
                {selectedIds.length > 0 && (
                    <span className="rounded-xl bg-primeColor px-3 py-2 text-xs font-bold text-white">
                        {selectedIds.length} selected
                    </span>
                )}
            </div>

            {/* Match List */}
            <div className="flex-1 overflow-y-auto flex flex-col gap-3 pr-0.5 max-h-[480px]">
                {loading && (
                    <div className="flex items-center justify-center gap-2 py-10 text-sm text-tpColor">
                        <HiOutlineArrowPath className="h-5 w-5 animate-spin text-primeColor" />
                        Finding matches with Jathaga Porutham…
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-borderColor bg-slate-50 px-4 py-8 text-center text-sm text-tpColor">
                        {matches.length === 0
                            ? "No matches found. Make sure this member's gender and star (nakshatra) are filled in their profile."
                            : "No matches for the current search filter."}
                    </div>
                )}
                <AnimatePresence>
                    {!loading && filtered.map((m, idx) => {
                        const selected = selectedIds.includes(m._id)
                        const age = ageFrom(m.dateOfBirth)
                        const mobile = m.mobileNumber || m.contactInformation?.mobileNumber || ""
                        const star = m.starName || m.star || m.additionalInformation?.starName || ""
                        return (
                            <motion.div
                                key={m._id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.03 }}
                                onClick={() => toggleOne(m._id)}
                                className={`cursor-pointer rounded-2xl border p-4 transition select-none ${selected ? "border-primeColor bg-primeSoft/40 shadow-sm" : "border-borderColor bg-white hover:border-primeColor/40 hover:bg-slate-50"}`}
                            >
                                <div className="flex items-start gap-3">
                                    {/* Checkbox */}
                                    <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition ${selected ? "border-primeColor bg-primeColor" : "border-borderColor bg-white"}`}>
                                        {selected && <div className="h-2 w-2 rounded-full bg-white" />}
                                    </div>
                                    {/* Info */}
                                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <p className="font-semibold text-tColor text-sm">
                                                    {m.firstName} {m.surname || m.lastName || ""}
                                                    <span className="ml-1.5 text-xs font-normal text-tpColor">· {m.refId}</span>
                                                </p>
                                                <p className="text-xs text-tpColor mt-0.5">
                                                    {[age && `${age} yrs`, m.gender, star && `★ ${star}`, mobile].filter(Boolean).join(" · ")}
                                                </p>
                                            </div>
                                            {m.porutham && (
                                                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-bold ${vs(m.porutham.verdict).pill}`}>
                                                    {m.porutham.total}/{m.porutham.max}
                                                </span>
                                            )}
                                        </div>
                                        <PoruthamBar porutham={m.porutham} />
                                    </div>
                                </div>
                            </motion.div>
                        )
                    })}
                </AnimatePresence>
            </div>

            {/* Send Footer */}
            <div className="sticky bottom-0 border-t border-borderColor bg-white pt-3">
                {!whatsappNumber && (
                    <p className="mb-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 font-medium">
                        ⚠️ No WhatsApp number found for this member. Please update their profile first.
                    </p>
                )}
                <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-tpColor">
                        {selectedIds.length > 0
                            ? `Sending ${selectedIds.length} profile${selectedIds.length > 1 ? "s" : ""} to ${targetUser.firstName}`
                            : "Select profiles above to send"}
                    </p>
                    <button
                        type="button"
                        disabled={!whatsappNumber || selectedIds.length === 0 || sending}
                        onClick={sendOnWhatsApp}
                        className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-[#1ebe5c] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <RiWhatsappLine className="h-4 w-4" />
                        {sending ? "Opening WhatsApp…" : "Send via WhatsApp"}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Main AdminMatchFinder ─────────────────────────────────────────────────────
const AdminMatchFinder = () => {
    const [selectedUser, setSelectedUser] = useState(null)

    return (
        <motion.div className="space-y-5">
            {/* Page header */}
            <div className="panel-card p-5 flex flex-col gap-1">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Admin Tool</p>
                <h2 className="text-xl font-semibold text-tColor">Find Matches with Jathaga Porutham</h2>
                <p className="text-sm text-tpColor">
                    Select a member → view matched profiles ranked by Jathaga Porutham score → send selected profiles to their WhatsApp.
                </p>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
                {/* Left — user selector */}
                <div className="panel-card p-5">
                    <UserSearchPanel onSelect={(user) => { setSelectedUser(user) }} />
                </div>

                {/* Right — match results */}
                <div className="panel-card p-5 min-h-[520px]">
                    <AnimatePresence mode="wait">
                        {!selectedUser ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex h-full min-h-[420px] flex-col items-center justify-center gap-3 text-center"
                            >
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                                    <HiOutlineSparkles className="h-8 w-8 text-tpColor" />
                                </div>
                                <p className="text-sm font-semibold text-tColor">No member selected</p>
                                <p className="text-xs text-tpColor max-w-[220px]">
                                    Search and select a member on the left to see their Jathaga Porutham matches here.
                                </p>
                            </motion.div>
                        ) : (
                            <motion.div
                                key={selectedUser._id}
                                initial={{ opacity: 0, x: 16 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -16 }}
                                className="h-full"
                            >
                                <MatchResultsPanel
                                    targetUser={selectedUser}
                                    onBack={() => setSelectedUser(null)}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    )
}

export default AdminMatchFinder
